// Libraries.
import { mergeAllWith, isArray, isPlainObject } from 'lodash/fp'
import paramParser from 'query-string'
import { call, put } from 'redux-saga/effects'

/**
 * Deeply merges the values of multiple objects. Objects on the left receive the values from objects on their right.
 * Unlike lodash's default merge behavior this doesn't merge arrays.
 *
 * @name mergeValues
 * @param {...Object} objects - Objects to merge
 * @return {Object} A new object containing the merged values.
 */
export function mergeValues (...objects) {
  return mergeAllWith((leftValue, rightValue) => {
    // Overwrite the default behavior of lodash's merge for arrays and simply
    // clobber what's on the left so we don't end up with merged arrays.
    if (isArray(leftValue)) {
      return rightValue
    }
  }, objects)
}

/**
 * Utility function to convert an object to a query string.
 *
 * @param {Object} params An object of query parameters to be parsed and converted for use in a URL string
 * @param {Object} [options] Options to be passed to the query-string library
 * * @param {String} [options.arrayFormat] Format in which to compose array values which were passed as query parameters
 */
export function toQueryString (params = {}, options = {}) {
  if (Object.keys(params).length > 0) {
    const stringifiedParams = paramParser.stringify(params, options)
    return '?' + stringifiedParams
  }
  return ''
}

/**
 * Higher-order function to auto-restart sagas when they crash.
 * Based on: https://github.com/redux-saga/redux-saga/pull/644#issuecomment-266454875
 * @method autoRestart
 * @param  {Generator} saga The saga to wrap.
 * @return {Generator} Wrapped saga.
 */
export function autoRestart (saga) {
  return function * autoRestarting (...args) {
    // Only restart the saga if it crashed; avoid restarting it if
    //      it returned normally.
    let shouldRestart = false
    do {
      try {
        yield call(saga, ...args)
        shouldRestart = false
      } catch (e) {
        // TODO: Re-add this log line.
        // Importing the LogManager in this file breaks tests for an unknown
        //    reason. Should find out why so that we can log in our utils.
        // log.error(`Unhandled error in saga ${saga.name}.`, e)
        console.log(`Unhandled error in saga ${saga.name}.`, e)
        shouldRestart = true
      }
    } while (shouldRestart)
  }
}

/**
 * Forwards the actions by directly dispatching them.
 * For example, this can be used with a takeEvery effect to grab actions from a channel and dispatch them.
 * @param {Object} action The action to be forwarded.
 * @example
 * const channel = eventChannel(...)
 * yield takeEvery(channel, forwardAction)
 */
export function * forwardAction (action) {
  yield put(action)
}

/**
 * Ensures that services are in the same format understood by the server regardless,
 * of whether the client provides services as strings or objects.
 * @param {Array} services The list of services requested by the client.
 * @return {Array} A normalized list of services requested by the client.
 */
export function normalizeServices (services = []) {
  return services.map(service => {
    if (isPlainObject(service) && service.hasOwnProperty('service')) {
      return service
    }
    return { service: service }
  })
}

/**
 * A deferred promise
 * Don't use this unless you know what you are doing:
 *  <https://github.com/petkaantonov/bluebird/wiki/Promise-anti-patterns#the-deferred-anti-pattern>
 * @returns An object a with promise property and functions to resolve and reject
 */
export function defer () {
  const d = {}
  d.promise = new Promise((resolve, reject) => {
    d.resolve = resolve
    d.reject = reject
  })
  return d
}
